import { useMemo, useState } from "react";
import {
  ArrowDownLeft,
  ArrowUpRight,
  CalendarDays,
  Copy,
  Eye,
  MoreVertical,
  Search,
  SlidersHorizontal,
  Trash2,
  WalletCards,
  TrendingUp,
  ReceiptText,
  Target,
  X,
} from "lucide-react";
import { excluirMovimentacao } from "../services/financeService";
import { formatarMoeda } from "../utils/money";
import "../styles/pages/movimentacoes.css";

function formatarData(mov) {
  if (mov?.data?.toDate) return mov.data.toDate().toLocaleDateString("pt-BR");

  if (typeof mov?.data === "string") {
    if (mov.data.includes("-") && mov.data.length >= 10) {
      const [ano, mes, dia] = mov.data.slice(0, 10).split("-");
      if (ano && mes && dia) return `${dia}/${mes}/${ano}`;
    }
    return mov.data;
  }

  return "-";
}

function calcularCategoriaTop(movimentacoes) {
  const despesas = movimentacoes.filter((mov) => mov.tipo === "despesa");

  if (despesas.length === 0) {
    return { categoria: "Sem despesas", valor: 0 };
  }

  const totalPorCategoria = despesas.reduce((acc, mov) => {
    const categoria = mov.categoria || "Sem categoria";
    acc[categoria] = (acc[categoria] || 0) + Number(mov.valor || 0);
    return acc;
  }, {});

  const [categoria, valor] = Object.entries(totalPorCategoria).sort((a, b) => b[1] - a[1])[0];
  return { categoria, valor };
}

function maiorMovimento(movimentacoes, tipo) {
  const lista = movimentacoes.filter((mov) => mov.tipo === tipo);
  if (lista.length === 0) return null;
  return [...lista].sort((a, b) => Number(b.valor || 0) - Number(a.valor || 0))[0];
}

export default function Movimentacoes({ uid, movimentacoes = [] }) {
  const [mostrarTudo, setMostrarTudo] = useState(false);
  const [busca, setBusca] = useState("");
  const [filtro, setFiltro] = useState("todas");
  const [menuAbertoId, setMenuAbertoId] = useState(null);
  const [movDetalhe, setMovDetalhe] = useState(null);
  const [mensagem, setMensagem] = useState("");

  const listaFiltrada = useMemo(() => {
    return movimentacoes.filter((mov) => {
      const texto = `${mov.descricao || ""} ${mov.categoria || ""} ${mov.tipo || ""} ${mov.valor || ""}`.toLowerCase();
      const bateBusca = texto.includes(busca.trim().toLowerCase());

      if (!bateBusca) return false;
      if (filtro === "receitas") return mov.tipo === "receita";
      if (filtro === "despesas") return mov.tipo === "despesa";
      return true;
    });
  }, [movimentacoes, busca, filtro]);

  const movimentacoesVisiveis = mostrarTudo ? listaFiltrada : listaFiltrada.slice(0, 6);

  const entradas = movimentacoes
    .filter((mov) => mov.tipo === "receita")
    .reduce((acc, mov) => acc + Number(mov.valor || 0), 0);

  const saidas = movimentacoes
    .filter((mov) => mov.tipo === "despesa")
    .reduce((acc, mov) => acc + Number(mov.valor || 0), 0);

  const saldo = entradas - saidas;
  const categoriaTop = calcularCategoriaTop(movimentacoes);
  const maiorReceita = maiorMovimento(movimentacoes, "receita");
  const maiorDespesa = maiorMovimento(movimentacoes, "despesa");

  const mediaMovimento =
    movimentacoes.length > 0
      ? movimentacoes.reduce((acc, mov) => acc + Number(mov.valor || 0), 0) / movimentacoes.length
      : 0;

  function mostrarMensagem(texto) {
    setMensagem(texto);
    setTimeout(() => setMensagem(""), 1800);
  }

  function copiarMovimentacao(mov) {
    const texto = `${mov.tipo === "receita" ? "Receita" : "Despesa"} - ${mov.descricao || "Sem descrição"} | ${mov.categoria || "Sem categoria"} | ${formatarMoeda(mov.valor)} | ${formatarData(mov)}`;
    navigator.clipboard?.writeText(texto);
    setMenuAbertoId(null);
    mostrarMensagem("Movimentação copiada.");
  }

  async function excluirComMenu(mov) {
    await excluirMovimentacao(uid, mov.id);
    setMenuAbertoId(null);
    mostrarMensagem("Movimentação excluída.");
  }

  return (
    <section className="mov-premium-page">
      <div className="mov-page-heading">
        <div>
          <span>Financeiro</span>
          <h2>Movimentações</h2>
          <p>Controle receitas, despesas e histórico financeiro do mês.</p>
        </div>

        <div className="mov-heading-icon">
          <WalletCards size={24} />
        </div>
      </div>

      {mensagem && <div className="mov-toast">{mensagem}</div>}

      <div className="mov-summary-grid">
        <MovSummaryCard type="green" icon={<ArrowUpRight size={24} />} label="Entradas" value={formatarMoeda(entradas)} caption="Receitas do mês" />
        <MovSummaryCard type="red" icon={<ArrowDownLeft size={24} />} label="Saídas" value={formatarMoeda(saidas)} caption="Despesas do mês" />
        <MovSummaryCard type={saldo >= 0 ? "blue" : "red"} icon={<TrendingUp size={24} />} label="Resultado" value={formatarMoeda(saldo)} caption={saldo >= 0 ? "Mês positivo" : "Atenção no saldo"} />
        <MovSummaryCard type="purple" icon={<WalletCards size={24} />} label="Total" value={movimentacoes.length} caption="Lançamentos salvos" />
      </div>

      <div className="mov-insights-grid">
        <MovInsightCard type="green" icon={<ArrowUpRight size={22} />} label="Maior receita" value={maiorReceita ? formatarMoeda(maiorReceita.valor) : formatarMoeda(0)} caption={maiorReceita?.descricao || "Nenhuma receita ainda"} />
        <MovInsightCard type="red" icon={<ReceiptText size={22} />} label="Maior despesa" value={maiorDespesa ? formatarMoeda(maiorDespesa.valor) : formatarMoeda(0)} caption={maiorDespesa?.descricao || "Nenhuma despesa ainda"} />
        <MovInsightCard type="purple" icon={<Target size={22} />} label="Categoria top" value={categoriaTop.categoria} caption={`${formatarMoeda(categoriaTop.valor)} consumidos`} />
        <MovInsightCard type="blue" icon={<TrendingUp size={22} />} label="Média por lançamento" value={formatarMoeda(mediaMovimento)} caption="Volume médio registrado" />
      </div>

      <section className="mov-table-card">
        <div className="mov-table-header">
          <div>
            <h2>Histórico do mês</h2>
            <span>Mostrando {movimentacoesVisiveis.length} de {listaFiltrada.length} lançamento(s)</span>
          </div>

          <div className="mov-tools">
            <label className="mov-filter">
              <SlidersHorizontal size={16} />
              <select value={filtro} onChange={(e) => setFiltro(e.target.value)}>
                <option value="todas">Todas</option>
                <option value="receitas">Receitas</option>
                <option value="despesas">Despesas</option>
              </select>
            </label>

            <label className="mov-search">
              <Search size={17} />
              <input placeholder="Buscar movimento..." value={busca} onChange={(e) => setBusca(e.target.value)} />
            </label>
          </div>
        </div>

        {listaFiltrada.length === 0 ? (
          <div className="mov-empty-state">Nenhuma movimentação encontrada.</div>
        ) : (
          <>
            <div className="mov-table">
              <div className="mov-table-head">
                <span>Descrição</span>
                <span>Categoria</span>
                <span>Tipo</span>
                <span>Data</span>
                <span>Valor</span>
                <span></span>
              </div>

              {movimentacoesVisiveis.map((mov) => (
                <article className="mov-row" key={mov.id}>
                  <div className={mov.tipo === "receita" ? "mov-row-icon income" : "mov-row-icon expense"}>
                    {mov.tipo === "receita" ? "+" : "-"}
                  </div>

                  <div className="mov-row-main">
                    <strong>{mov.descricao || "Sem descrição"}</strong>
                  </div>

                  <span className="mov-row-category">{mov.categoria || "Sem categoria"}</span>

                  <span className={mov.tipo === "receita" ? "mov-badge income" : "mov-badge expense"}>
                    {mov.tipo === "receita" ? "Receita" : "Despesa"}
                  </span>

                  <span className="mov-row-date">
                    <CalendarDays size={15} />
                    {formatarData(mov)}
                  </span>

                  <strong className={mov.tipo === "receita" ? "mov-row-money income" : "mov-row-money expense"}>
                    {mov.tipo === "receita" ? "+" : "-"} {formatarMoeda(mov.valor)}
                  </strong>

                  <div className="mov-row-actions">
                    <button className="mov-danger-btn" onClick={() => excluirMovimentacao(uid, mov.id)} type="button" aria-label="Excluir movimentação">
                      <Trash2 size={16} />
                    </button>

                    <div className="mov-actions-menu-wrap">
                      <button
                        className="mov-more-btn"
                        type="button"
                        aria-label="Mais opções"
                        onClick={() => setMenuAbertoId((atual) => (atual === mov.id ? null : mov.id))}
                      >
                        <MoreVertical size={16} />
                      </button>

                      {menuAbertoId === mov.id && (
                        <div className="mov-actions-menu">
                          <button type="button" onClick={() => setMovDetalhe(mov)}>
                            <Eye size={16} />
                            Ver detalhes
                          </button>

                          <button type="button" onClick={() => copiarMovimentacao(mov)}>
                            <Copy size={16} />
                            Copiar resumo
                          </button>

                          <button type="button" className="danger" onClick={() => excluirComMenu(mov)}>
                            <Trash2 size={16} />
                            Excluir
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </article>
              ))}
            </div>

            {listaFiltrada.length > 6 && (
              <button className="mov-show-more" onClick={() => setMostrarTudo((atual) => !atual)} type="button">
                {mostrarTudo ? "Mostrar menos" : "Ver histórico completo"}
              </button>
            )}
          </>
        )}
      </section>

      {movDetalhe && (
        <div className="mov-detail-backdrop" onClick={() => setMovDetalhe(null)}>
          <section className="mov-detail-card" onClick={(e) => e.stopPropagation()}>
            <div className="mov-detail-header">
              <div>
                <span>{movDetalhe.tipo === "receita" ? "Receita" : "Despesa"}</span>
                <h2>{movDetalhe.descricao || "Sem descrição"}</h2>
              </div>

              <button type="button" onClick={() => setMovDetalhe(null)}>
                <X size={19} />
              </button>
            </div>

            <div className="mov-detail-grid">
              <div><span>Valor</span><strong>{formatarMoeda(movDetalhe.valor)}</strong></div>
              <div><span>Categoria</span><strong>{movDetalhe.categoria || "Sem categoria"}</strong></div>
              <div><span>Data</span><strong>{formatarData(movDetalhe)}</strong></div>
              <div><span>Tipo</span><strong>{movDetalhe.tipo === "receita" ? "Receita" : "Despesa"}</strong></div>
            </div>

            {movDetalhe.observacao && (
              <div className="mov-detail-note">
                <span>Observação</span>
                <p>{movDetalhe.observacao}</p>
              </div>
            )}
          </section>
        </div>
      )}
    </section>
  );
}

function MovSummaryCard({ type, icon, label, value, caption }) {
  return (
    <article className={`mov-summary-card ${type}`}>
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
        <p>{caption}</p>
      </div>
      <div className="mov-card-icon">{icon}</div>
    </article>
  );
}

function MovInsightCard({ type, icon, label, value, caption }) {
  return (
    <article className={`mov-insight-card ${type}`}>
      <div className="mov-insight-icon">{icon}</div>
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
        <p>{caption}</p>
      </div>
    </article>
  );
}
